import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "src/utils/cn";
import { useAuth } from "src/contexts/AuthContext";
import { appUrl } from "src/config/hosts";
import { ClozdlMark } from "src/components/ClozdlMark";

export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollTo = (elementId) => {
    setMenuOpen(false);
    setDropdownOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${elementId}`);
    } else {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300 border-b",
        scrolled
          ? "bg-[#05081D]/90 backdrop-blur-md border-white/10 shadow-[0_1px_15px_rgba(0,0,0,0.3)]"
          : "bg-transparent border-transparent"
      )}
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <nav className="flex h-16 items-center justify-between sm:h-[76px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" aria-label="Clozdl home">
            <ClozdlMark size={34} color="#C9B482" />
            <span className="text-xl font-bold tracking-tight text-white">
              Clozdl<span className="text-[#C9B482]">.ai</span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="hidden items-center gap-8 md:flex" role="list">
            <li>
              <Link
                to="/"
                className="text-[15px] font-semibold text-white/80 hover:text-white transition-colors"
              >
                Home
              </Link>
            </li>
            <li className="relative">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                className="flex items-center gap-1 text-[15px] font-semibold text-white/80 hover:text-white transition-colors focus:outline-none"
              >
                Products
                <ChevronDown className="h-4 w-4 opacity-70" />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-xl border border-white/10 bg-[#070b24] p-2 shadow-2xl backdrop-blur-xl">
                  <button
                    onClick={() => handleScrollTo("pre-sales")}
                    className="block w-full rounded-lg px-3 py-2 text-left text-[14px] font-medium text-white/70 hover:bg-white/5 hover:text-white transition-all"
                  >
                    Pre-Sales
                  </button>
                  <button
                    onClick={() => handleScrollTo("sales")}
                    className="block w-full rounded-lg px-3 py-2 text-left text-[14px] font-medium text-white/70 hover:bg-white/5 hover:text-white transition-all"
                  >
                    Sales
                  </button>
                  <button
                    onClick={() => handleScrollTo("post-sales")}
                    className="block w-full rounded-lg px-3 py-2 text-left text-[14px] font-medium text-white/70 hover:bg-white/5 hover:text-white transition-all"
                  >
                    Post-Sales
                  </button>
                </div>
              )}
            </li>
            <li>
              <button
                onClick={() => handleScrollTo("contact")}
                className="text-[15px] font-semibold text-white/80 hover:text-white transition-colors"
              >
                Contact Us
              </button>
            </li>
          </ul>

          {/* CTA Group */}
          <div className="hidden items-center gap-6 md:flex">
            {isAuthenticated ? (
              <a
                href={appUrl("/dashboard")}
                className="text-[15px] font-semibold text-white/80 hover:text-white transition-colors"
              >
                Dashboard
              </a>
            ) : (
              <a
                href={appUrl("/login")}
                className="text-[15px] font-semibold text-white/80 hover:text-white transition-colors"
              >
                Log in
              </a>
            )}
            <button
              onClick={() => handleScrollTo("contact")}
              className="gradient-button inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#4B73FF] to-[#7c6fff] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(75,115,255,0.4)] transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            >
              Book Demo
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10 md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="border-t border-white/10 bg-[#05081D]/95 px-4 pb-6 pt-4 backdrop-blur-lg md:hidden">
          <ul className="flex flex-col gap-2" role="list">
            <li>
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="block rounded-lg px-3 py-2 text-[15px] font-medium text-white/80 hover:bg-white/5 hover:text-white"
              >
                Home
              </Link>
            </li>
            <li className="border-t border-white/5 pt-2">
              <span className="block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
                Products
              </span>
              <div className="pl-3 mt-1 flex flex-col gap-1">
                <button
                  onClick={() => handleScrollTo("pre-sales")}
                  className="block rounded-lg px-3 py-2 text-left text-[14px] text-white/70 hover:bg-white/5 hover:text-white"
                >
                  Pre-Sales
                </button>
                <button
                  onClick={() => handleScrollTo("sales")}
                  className="block rounded-lg px-3 py-2 text-left text-[14px] text-white/70 hover:bg-white/5 hover:text-white"
                >
                  Sales
                </button>
                <button
                  onClick={() => handleScrollTo("post-sales")}
                  className="block rounded-lg px-3 py-2 text-left text-[14px] text-white/70 hover:bg-white/5 hover:text-white"
                >
                  Post-Sales
                </button>
              </div>
            </li>
            <li className="border-t border-white/5 pt-2">
              <button
                onClick={() => handleScrollTo("contact")}
                className="block w-full rounded-lg px-3 py-2 text-left text-[15px] font-medium text-white/80 hover:bg-white/5 hover:text-white"
              >
                Contact Us
              </button>
            </li>
          </ul>

          <div className="mt-6 flex flex-col gap-3 border-t border-white/5 pt-4">
            {isAuthenticated ? (
              <a
                href={appUrl("/dashboard")}
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center justify-center rounded-lg border border-white/10 py-2.5 text-[15px] font-semibold text-white/80 hover:bg-white/5"
              >
                Dashboard
              </a>
            ) : (
              <a
                href={appUrl("/login")}
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center justify-center rounded-lg border border-white/10 py-2.5 text-[15px] font-semibold text-white/80 hover:bg-white/5"
              >
                Log in
              </a>
            )}
            <button
              onClick={() => handleScrollTo("contact")}
              className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-[#4B73FF] to-[#7c6fff] py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(75,91,237,0.35)]"
            >
              Book Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
